import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import SpecOutDto from '@/models/SpecOuts/SpecOutDto';
import SpecOutContactDto from '@/models/SpecOuts/SpecOutContactDto';
import UpdateSpecOutGeneralDetailsRequest from '@/models/SpecOuts/UpdateSpecOutGeneralDetailsRequest';
import UpdateSpecOutEmailDetailsRequest from '@/models/SpecOuts/UpdateSpecOutEmailDetailsRequest';
import AddSpecOutContactsRequest from '@/models/SpecOuts/AddSpecOutContactsRequest';
import SpecOutBrowseContactsViewModel from '@/models/SpecOuts/SpecOutBrowseContactsViewModel';
import CampaignEmailPreviewResponse from '@/models/CampaignContacts/CampaignEmailPreviewResponse';
import WorkerProfileDto from '@/models/Workers/WorkerProfileDto';
import WorkerProfileSearchRequest from '@/models/Workers/WorkerProfileSearchRequest';
import WorkerStatus from '@/models/Workers/WorkerStatus';
import ContactProfileDto from '@/models/Contacts/ContactProfileDto';
import ContactProfileSearchRequest from '@/models/Contacts/ContactProfileSearchRequest';
import ContactStatus from '@/models/Contacts/ContactStatus';
import ContactSearchViewModel from '@/models/Contacts/ContactSearchViewModel';
import PositionDto from '@/models/Positions/PositionDto';
import LocationTypeDto from '@/models/LocationTypes/LocationTypeDto';
import SelectableEntity from '@/models/Shared/SelectableEntity';

export interface ISpecOutDetailsState {
  loading: boolean;
  loadingWorkers: boolean;
  loadingContacts: boolean;
  loadingDialog: boolean;
  currentSpecOut: SpecOutDto | null;
  specOutContacts: SpecOutContactDto[];
  browseContactsVM: SpecOutBrowseContactsViewModel;
  contactSearchViewModel: ContactSearchViewModel;
  workerProfiles: WorkerProfileDto[];
}

@Module({ dynamic: true, store, name: 'SpecOutDetails', namespaced: true})
class SpecOutDetails extends VuexModule implements ISpecOutDetailsState {

  public loading = false;
  public loadingWorkers = false;
  public loadingContacts = false;
  public loadingDialog = false;
  public currentSpecOut: SpecOutDto = new SpecOutDto();
  public specOutContacts: SpecOutContactDto[] = [];
  public browseContactsVM: SpecOutBrowseContactsViewModel = new SpecOutBrowseContactsViewModel();
  public contactSearchViewModel: ContactSearchViewModel = new ContactSearchViewModel();
  public workerProfiles: WorkerProfileDto[] = [];

  // ==========================================================================
  // Spec Out CRUD
  // ==========================================================================

  @Action
  public async getSpecOut(specOutId: string) {
    this.setLoading(true);
    await ajax.get(`/api/v1/agency/spec-outs/${specOutId}`)
    .then(response => {
      this.context.commit("setCurrentSpecOut", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoading(false);
    });
  }

  @Action
  public async editSpecOutGeneralDetails(request: UpdateSpecOutGeneralDetailsRequest) {
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/spec-outs/${this.currentSpecOut.id}/general-details`, request)
    .then(response => {
      this.context.commit("setCurrentSpecOutFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  public async editSpecOutEmailDetails(request: UpdateSpecOutEmailDetailsRequest) {
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/spec-outs/${this.currentSpecOut.id}/email-details`, request)
    .then(response => {
      this.context.commit("setCurrentSpecOutFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  public async deleteSpecOut() {
    this.setLoadingDialog(true);
    await ajax.delete(`/api/v1/agency/spec-outs/${this.currentSpecOut.id}`)
    .then(() => {
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  public async sendSpecOut() {
    this.setLoadingDialog(true);
    await ajax.post(`/api/v1/agency/spec-outs/${this.currentSpecOut.id}/send`, null)
    .then(response => {
      this.context.commit("setCurrentSpecOutFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  // ==========================================================================
  // Preview
  // ==========================================================================

  @Action
  public async previewSpecOutContactEmail(specOutContactId: string): Promise<CampaignEmailPreviewResponse | null> {
    this.setLoadingDialog(true);
    try {
      const response = await ajax.get(
        `/api/v1/agency/spec-outs/${this.currentSpecOut.id}/contacts/${specOutContactId}/preview`
      );
      this.setLoadingDialog(false);
      return response.data as CampaignEmailPreviewResponse;
    } catch (error) {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
      return null;
    }
  }

  // ==========================================================================
  // Contacts
  // ==========================================================================

  @Action
  public async getSpecOutContacts() {
    this.setLoadingContacts(true);
    await ajax.get(`/api/v1/agency/spec-outs/${this.currentSpecOut.id}/contacts`)
    .then(response => {
      this.context.commit("setSpecOutContacts", response.data);
      this.setLoadingContacts(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingContacts(false);
    });
  }

  @Action
  public async addSpecOutContacts(request: AddSpecOutContactsRequest) {
    this.setLoadingDialog(true);
    await ajax.post(`/api/v1/agency/spec-outs/${this.currentSpecOut.id}/contacts`, request)
    .then(() => {
      this.context.commit("clearBrowseSelectedContactIds");
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  public async removeSpecOutContact(contactId: string) {
    this.setLoadingDialog(true);
    await ajax.delete(`/api/v1/agency/spec-outs/${this.currentSpecOut.id}/contacts/${contactId}`)
    .then(() => {
      this.context.commit("setRemoveSpecOutContact", contactId);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  public async browseContactSearch() {
    this.setLoadingContacts(true);
    const request = new ContactProfileSearchRequest();

    request.status = this.contactSearchViewModel.selectedStatus;
    request.isAgreedToMarketing = true;

    // Location Types
    request.locationTypeIds = this.contactSearchViewModel.selectedLocationTypes.map(function(locationType: LocationTypeDto) {
      return locationType.id;
    });

    // Positions
    request.positionCodes = this.contactSearchViewModel.selectedPositions.map(function(position: PositionDto) {
      return position.code;
    });

    // Distance from worker
    if (this.currentSpecOut.worker != null) {
      request.distance = this.contactSearchViewModel.selectedDistance.value;
      if (request.distance != 0) {
        request.latitude = this.currentSpecOut.worker.geometry.lat;
        request.longitude = this.currentSpecOut.worker.geometry.lng;
      }
    }

    await ajax.post(`/api/v1/agency/contacts/search`, request)
    .then(response => {
      if (response.data == null) {
        response.data = [];
      }
      this.context.commit("setBrowseContactProfiles", response.data);
      this.setLoadingContacts(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingContacts(false);
    });
  }

  @Action
  public async updateSelectedBrowseContact(id: string) {
    const contactProfile = this.browseContactsVM.contactProfiles.find((c: SelectableEntity<ContactProfileDto>) => id === c.entity.id);
    if (contactProfile) {
      this.context.commit("setBrowseContact", contactProfile.entity);
    } else {
      this.context.commit("setBrowseContact", null);
    }
  }

  @Action
  public async addBrowseSelectedContactId(contactId: string) {
    this.context.commit("setAddBrowseSelectedContactId", contactId);
  }

  @Action
  public async removeBrowseSelectedContactId(contactId: string) {
    this.context.commit("setRemoveBrowseSelectedContactId", contactId);
  }

  // ==========================================================================
  // Worker Profile Search (for single-worker selector)
  // ==========================================================================

  @Action
  public async searchWorkerProfiles() {
    const request = new WorkerProfileSearchRequest();
    request.statuses = [WorkerStatus.Active];

    await ajax.post(`/api/v1/agency/workers/search`, request)
    .then(response => {
      if (response.data == null) {
        response.data = [];
      }
      this.context.commit("setWorkerProfiles", response.data);
    })
    .catch(error => {
      AppModule.handleApiError(error);
    });
  }

  // ==========================================================================
  // Clean state
  // ==========================================================================

  @Action
  cleanState() {
    this.context.commit("resetState");
  }

  // ==========================================================================
  // Mutations
  // ==========================================================================

  @Mutation
  resetState() {
    this.loading = false;
    this.loadingWorkers = false;
    this.loadingContacts = false;
    this.loadingDialog = false;
    this.currentSpecOut = new SpecOutDto();
    this.specOutContacts = [];
    this.browseContactsVM = new SpecOutBrowseContactsViewModel();
    this.contactSearchViewModel = new ContactSearchViewModel();
    this.workerProfiles = [];
  }

  @Mutation
  private async setLoading(isLoading: boolean) {
    this.loading = isLoading;
  }

  @Mutation
  private async setLoadingWorkers(isLoading: boolean) {
    this.loadingWorkers = isLoading;
  }

  @Mutation
  private async setLoadingContacts(isLoading: boolean) {
    this.loadingContacts = isLoading;
  }

  @Mutation
  private async setLoadingDialog(isLoading: boolean) {
    this.loadingDialog = isLoading;
  }

  @Mutation
  private async setCurrentSpecOut(specOut: SpecOutDto) {
    this.currentSpecOut = specOut;
  }

  @Mutation
  private async setCurrentSpecOutFromEdit(specOut: SpecOutDto) {
    this.currentSpecOut.updatedAt = specOut.updatedAt;
    this.currentSpecOut.branchId = specOut.branchId;
    this.currentSpecOut.branch = specOut.branch;
    this.currentSpecOut.deskId = specOut.deskId;
    this.currentSpecOut.desk = specOut.desk;
    this.currentSpecOut.ownerId = specOut.ownerId;
    this.currentSpecOut.owner = specOut.owner;
    this.currentSpecOut.emailTemplateId = specOut.emailTemplateId;
    this.currentSpecOut.emailTemplate = specOut.emailTemplate;
    this.currentSpecOut.emailBccUser = specOut.emailBccUser;
    this.currentSpecOut.status = specOut.status;
    this.currentSpecOut.sentAt = specOut.sentAt;
    this.currentSpecOut.workerId = specOut.workerId;
    this.currentSpecOut.worker = specOut.worker;
  }

  // Contacts mutations
  @Mutation
  private async setSpecOutContacts(contacts: SpecOutContactDto[]) {
    this.specOutContacts = contacts;
  }

  @Mutation
  private async setRemoveSpecOutContact(contactId: string) {
    const index = this.specOutContacts.findIndex((c: SpecOutContactDto) => c.contactId === contactId);
    if (index >= 0) {
      this.specOutContacts.splice(index, 1);
    }
  }

  @Mutation
  private async setBrowseContactProfiles(contactProfiles: ContactProfileDto[]) {
    this.browseContactsVM.contactProfiles = contactProfiles.map(function(contactProfile) {
      return new SelectableEntity<ContactProfileDto>(contactProfile);
    });
  }

  @Mutation
  private async setBrowseContact(contactProfile: ContactProfileDto) {
    if (contactProfile) {
      this.browseContactsVM.selectedContact = contactProfile;
    }
  }

  @Mutation
  private async setAddBrowseSelectedContactId(contactId: string) {
    this.browseContactsVM.selectedContactIds.push(contactId);
  }

  @Mutation
  private async setRemoveBrowseSelectedContactId(contactId: string) {
    const index = this.browseContactsVM.selectedContactIds.indexOf(contactId);
    this.browseContactsVM.selectedContactIds.splice(index, 1);
  }

  @Mutation
  private async clearBrowseSelectedContactIds() {
    this.browseContactsVM.selectedContactIds = [];
  }

  // Worker profiles mutation (for single-worker selector)
  @Mutation
  private async setWorkerProfiles(profiles: WorkerProfileDto[]) {
    this.workerProfiles = profiles;
  }
}

export const SpecOutDetailsModule = getModule(SpecOutDetails)
